import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../hooks/useAuth";
import {
  getUserTeams,
  getInvoicesForReport,
  formatCurrency,
} from "../services/firebaseService";
import { DateRange, Invoice, Team } from "../types";
import "./PaidSummaryReport.css";

const PaidSummaryReport: React.FC = () => {
  const { user } = useAuth();
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange>({ startDate: "", endDate: "" });

  const loadUserTeams = async () => {
    try {
      const userTeams = await getUserTeams(user!.uid);
      setTeams(userTeams);
      setSelectedTeams([]);
    } catch (error) {
      console.error("Error loading teams:", error);
    }
  };

  useEffect(() => {
    if (user) {
      loadUserTeams();
      setInitialDateRange();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const setInitialDateRange = () => {
    const currentDay = new Date().getDate();
    const currentDate = new Date();
    const lastMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1);
    const nextMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1);

    let startDate, endDate;
    if (currentDay < 26) {
      startDate = `${lastMonth.getFullYear()}-${String(lastMonth.getMonth() + 1).padStart(2, "0")}-26`;
      endDate = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, "0")}-25`;
    } else {
      startDate = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, "0")}-26`;
      endDate = `${nextMonth.getFullYear()}-${String(nextMonth.getMonth() + 1).padStart(2, "0")}-25`;
    }
    setDateRange({ startDate, endDate });
  };

  const loadReportData = async () => {
    if (selectedTeams.length === 0 || !dateRange.startDate || !dateRange.endDate) {
      alert("Selecione pelo menos uma equipe e defina o período.");
      return;
    }
    setLoading(true);
    try {
      const allInvoices: Invoice[] = [];
      for (const teamId of selectedTeams) {
        const teamInvoices = await getInvoicesForReport(teamId, dateRange.startDate, dateRange.endDate);
        allInvoices.push(...teamInvoices);
      }
      setInvoices(allInvoices);
    } catch (error) {
      console.error("Error loading report data:", error);
      alert("Erro ao carregar dados do relatório.");
    } finally {
      setLoading(false);
    }
  };

  const { totalPaidValue, paidInvoicesCount, totalInvoicesCount } = useMemo(() => {
    let paidCount = 0;
    let paidSum = 0;
    const totalCount = invoices.length;
    for (const inv of invoices) {
      if (inv.paid) {
        paidCount += 1;
        paidSum += inv.totalWithFactor || inv.total || 0;
      }
    }
    return { totalPaidValue: paidSum, paidInvoicesCount: paidCount, totalInvoicesCount: totalCount };
  }, [invoices]);

  type TeamRow = { teamId: string; teamName: string; paidCount: number; totalCount: number; paidAmount: number; percentPaid: number };

  const teamRows: TeamRow[] = useMemo(() => {
    const rows: TeamRow[] = [];
    const selectedSet = new Set(selectedTeams);
    const byTeam = new Map<string, { paidCount: number; totalCount: number; paidAmount: number }>();

    for (const inv of invoices) {
      const t = inv.team;
      if (!byTeam.has(t)) byTeam.set(t, { paidCount: 0, totalCount: 0, paidAmount: 0 });
      const agg = byTeam.get(t)!;
      agg.totalCount += 1;
      if (inv.paid) {
        agg.paidCount += 1;
        agg.paidAmount += inv.totalWithFactor || inv.total || 0;
      }
    }

    // Build rows for selected teams only (keep 0s if no invoices found)
    const teamsToShow = teams.filter((t) => selectedSet.has(t.id));
    for (const t of teamsToShow) {
      const agg = byTeam.get(t.id) || { paidCount: 0, totalCount: 0, paidAmount: 0 };
      const percent = agg.totalCount > 0 ? (agg.paidCount / agg.totalCount) * 100 : 0;
      rows.push({
        teamId: t.id,
        teamName: t.name,
        paidCount: agg.paidCount,
        totalCount: agg.totalCount,
        paidAmount: agg.paidAmount,
        percentPaid: percent,
      });
    }

    // Sort by team id for stability
    rows.sort((a, b) => a.teamId.localeCompare(b.teamId));
    return rows;
  }, [invoices, teams, selectedTeams]);

  const exportToCSV = () => {
    const headers = [
      'Equipe',
      'Valor Total Pago',
      'Qtd. Pagos',
      'Qtd. Total',
      '% Pagos'
    ].join(';');

    const rows = teamRows.map(row => [
      `"${row.teamName}"`,
      row.paidAmount.toFixed(2),
      row.paidCount,
      row.totalCount,
      Math.round(row.percentPaid)
    ].join(';'));

    const csvContent = [headers, ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `relatorio-pagos-resumo-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleTeamToggle = (teamId: string) => {
    setSelectedTeams((prev) =>
      prev.includes(teamId) ? prev.filter((id) => id !== teamId) : [...prev, teamId]
    );
  };

  const handleSelectAllTeams = () => {
    setSelectedTeams(teams.map((t) => t.id));
  };

  const handleDeselectAllTeams = () => {
    setSelectedTeams([]);
  };

  return (
    <div className="paid-summary-report-container">
      <div className="report-header">
        <div className="header-content">
          <h1>Relatório Pagos (Resumo)</h1>
          <button onClick={() => window.close()} className="close-btn">
            <i className="fas fa-times"></i>
            Fechar
          </button>
        </div>
      </div>

      <div className="report-content">
        <div className="filters-section">
          <div className="filter-group">
            <div className="teams-header">
              <h3>Equipes</h3>
              <div className="team-controls">
                <button onClick={handleSelectAllTeams} className="control-btn">Selecionar Todas</button>
                <button onClick={handleDeselectAllTeams} className="control-btn">Desmarcar Todas</button>
              </div>
            </div>
            <div className="teams-grid">
              {teams.map((team) => (
                <label key={team.id} className="team-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedTeams.includes(team.id)}
                    onChange={() => handleTeamToggle(team.id)}
                  />
                  {team.name}
                </label>
              ))}
            </div>
          </div>

          <div className="filter-group">
            <h3>Período</h3>
            <div className="date-inputs">
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => setDateRange((p) => ({ ...p, startDate: e.target.value }))}
                className="date-input"
              />
              <span>até</span>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => setDateRange((p) => ({ ...p, endDate: e.target.value }))}
                className="date-input"
              />
            </div>
          </div>

          <div className="action-buttons">
            <button
              onClick={loadReportData}
              disabled={loading || selectedTeams.length === 0}
              className="load-btn"
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Carregando...
                </>
              ) : (
                <>
                  <i className="fas fa-search"></i>
                  Gerar Relatório
                </>
              )}
            </button>
            {invoices.length > 0 && (
              <button onClick={exportToCSV} className="export-btn">
                <i className="fas fa-download"></i>
                Exportar CSV
              </button>
            )}
          </div>
        </div>

        {invoices.length > 0 && (
          <div className="summary-section">
            <h3>Resumo</h3>
            <div className="summary-cards">
              <div className="summary-card">
                <div className="card-value">{paidInvoicesCount}</div>
                <div className="card-label">Boletins Pagos</div>
              </div>
              <div className="summary-card">
                <div className="card-value">{totalInvoicesCount}</div>
                <div className="card-label">Boletins Totais</div>
              </div>
              <div className="summary-card">
                <div className="card-value paid-value">{formatCurrency(totalPaidValue)}</div>
                <div className="card-label">Valor Pago</div>
              </div>
            </div>
          </div>
        )}

        {invoices.length > 0 && (
          <div className="results-section">
            <div className="results-header">
              <h3>Resumo por Equipe</h3>
            </div>
            <div className="table-wrapper">
              <table className="report-table">
                <thead>
                  <tr>
                    <th>Equipe</th>
                    <th>Valor Total Pago</th>
                    <th>Qtd. Pagos</th>
                    <th>Qtd. Total</th>
                    <th>% Pagos</th>
                  </tr>
                </thead>
                <tbody>
                  {teamRows.map((row) => (
                    <tr key={row.teamId}>
                      <td>{row.teamName}</td>
                      <td className="value-cell">{formatCurrency(row.paidAmount)}</td>
                      <td>{row.paidCount}</td>
                      <td>{row.totalCount}</td>
                      <td>{row.percentPaid.toFixed(0)}%</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td style={{ fontWeight: 'bold' }}>Total</td>
                    <td className="value-cell" style={{ fontWeight: 'bold' }}>{formatCurrency(teamRows.reduce((s, r) => s + r.paidAmount, 0))}</td>
                    <td style={{ fontWeight: 'bold' }}>{teamRows.reduce((s, r) => s + r.paidCount, 0)}</td>
                    <td style={{ fontWeight: 'bold' }}>{teamRows.reduce((s, r) => s + r.totalCount, 0)}</td>
                    <td style={{ fontWeight: 'bold' }}>
                      {(() => {
                        const paid = teamRows.reduce((s, r) => s + r.paidCount, 0);
                        const total = teamRows.reduce((s, r) => s + r.totalCount, 0);
                        return total > 0 ? `${Math.round((paid / total) * 100)}%` : '0%';
                      })()}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

        {invoices.length === 0 && !loading && (
          <div className="no-data">
            <i className="fas fa-money-bill-wave"></i>
            <h3>Nenhum dado carregado</h3>
            <p>Selecione equipes, período e clique em "Gerar Relatório".</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaidSummaryReport;
